import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumDocumentType implements BaseEnumType {
  private static allValues: EnumDocumentType[] = []; // make sure this is top-most
  
  public static none = new EnumDocumentType(0, "None", "enum_document_type_none");
  public static invoice = new EnumDocumentType(1, "Invoice", "enum_document_type_invoice");
  public static creditNote = new EnumDocumentType(2, "CreditNote", "enum_document_type_credit_note");
  public static returnableLabel = new EnumDocumentType(3, "ReturnableLabel", "enum_document_type_returnable_label");
  public static nonReturnableLabel = new EnumDocumentType(4, "NonReturnableLabel", "enum_document_type_non_returnable_label");
  public static ullageLabel = new EnumDocumentType(5, "UllageLabel", "enum_document_type_ullage_label");
  public static stockTakeSheet = new EnumDocumentType(6, "StockTakeSheet", "enum_document_type_stock_take_sheet");
  public static purchaseOrder = new EnumDocumentType(7, "PurchaseOrder", "enum_document_type_purchase_order");
  public static despatchNote = new EnumDocumentType(8, "DespatchNote", "enum_document_type_despatch_note");
  public static dutyReturn = new EnumDocumentType(9, "DutyReturn", "enum_document_type_duty_return");
  public static flatGrid = new EnumDocumentType(10, "FlatGrid", "enum_document_type_flat_grid");
  public static groupGrid = new EnumDocumentType(11, "GroupGrid", "enum_document_type_group_grid");
  public static invoiceDataExchange = new EnumDocumentType(12, "InvoiceDataExchange", "enum_document_type_invoice_data_exchange");
  public static deliverySummarySheet = new EnumDocumentType(13, "DeliverySummarySheet", "enum_document_type_delivery_summary_sheet");
  public static driverManifest = new EnumDocumentType(14, "DriverManifest", "enum_document_type_driver_manifest");
  public static materialLabel = new EnumDocumentType(15, "MaterialLabel", "enum_document_type_material_label");
  public static beerflexDeliveryNote = new EnumDocumentType(16, "BeerflexDeliveryNote", "enum_document_type_beerflex_delivery_note");
  public static priceList = new EnumDocumentType(17, "PriceList", "enum_document_type_price_list");
  public static outletPriceList = new EnumDocumentType(18, "OutletPriceList", "enum_document_type_outlet_price_list");
  public static germanBeerDutyReturn = new EnumDocumentType(19, "GermanBeerDutyReturn", "enum_document_type_german_beer_duty_return");
  public static australianDutyReturn = new EnumDocumentType(20, "AustralianDutyReturn", "enum_document_type_australian_duty_return");
  public static accountsPackageCsv = new EnumDocumentType(21, "AccountsPackageCsv", "enum_document_type_accounts_package_csv");
  public static stockTransferNote = new EnumDocumentType(22, "StockTransferNote", "enum_document_type_stock_transfer_note");
  public static usSmallBrewersReport = new EnumDocumentType(23, "UsSmallBrewersReport", "enum_document_type_us_small_brewers_report");
  public static palletManifest = new EnumDocumentType(24, "PalletManifest", "enum_document_type_pallet_manifest");
  public static palletLabel = new EnumDocumentType(25, "PalletLabel", "enum_document_type_pallet_label");
  public static despatchNotification = new EnumDocumentType(26, "DespatchNotification", "enum_document_type_despatch_notification");
  public static assemblyControlSheet = new EnumDocumentType(27, "AssemblyControlSheet", "enum_document_type_assembly_control_sheet");
  public static taskCreatedNotification = new EnumDocumentType(28, "TaskCreatedNotification", "enum_document_type_task_created_notification");
  public static taskModifiedNotification = new EnumDocumentType(29, "TaskModifiedNotification", "enum_document_type_task_modified_notification");
  public static tasksDueNotification = new EnumDocumentType(30, "TasksDueNotification", "enum_document_type_tasks_due_notification");
  public static addressLabel = new EnumDocumentType(31, "AddressLabel", "enum_document_type_address_label");
  public static ukBeerNewDutyReturn = new EnumDocumentType(32, "UkBeerNewDutyReturn", "enum_document_type_uk_beer_new_duty_return");
  public static ukWineAndOtherNewDutyReturn = new EnumDocumentType(33, "UkWineAndOtherNewDutyReturn", "enum_document_type_uk_wine_and_other_new_duty_return");
  public static proFormaInvoice = new EnumDocumentType(34, "ProFormaInvoice", "enum_document_type_pro_forma_invoice");
  public static processControlSheet = new EnumDocumentType(35, "ProcessControlSheet", "enum_document_type_process_control_sheet");
  public static stockAdjustment = new EnumDocumentType(36, "StockAdjustment", "enum_document_type_stock_adjustment");
  public static batchControlSheet = new EnumDocumentType(37, "BatchControlSheet", "enum_document_type_batch_control_sheet");
  public static labelOrderInvoice = new EnumDocumentType(38, "LabelOrderInvoice", "enum_document_type_label_order_invoice");
  public static orderPickingList = new EnumDocumentType(39, "OrderPickingList", "enum_document_type_order_picking_list");
  public static groupedPriceList = new EnumDocumentType(40, "GroupedPriceList", "enum_document_type_grouped_price_list");
  public static groupedOutletPriceList = new EnumDocumentType(41, "GroupedOutletPriceList", "enum_document_type_grouped_outlet_price_list");
  public static ullageRecord = new EnumDocumentType(42, "UllageRecord", "enum_document_type_ullage_record");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumDocumentType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isNone() { return this === EnumDocumentType.none; }
  public isInvoice() { return this === EnumDocumentType.invoice; }
  public isCreditNote() { return this === EnumDocumentType.creditNote; }
  public isReturnableLabel() { return this === EnumDocumentType.returnableLabel; }
  public isNonReturnableLabel() { return this === EnumDocumentType.nonReturnableLabel; }
  public isUllageLabel() { return this === EnumDocumentType.ullageLabel; }
  public isStockTakeSheet() { return this === EnumDocumentType.stockTakeSheet; }
  public isPurchaseOrder() { return this === EnumDocumentType.purchaseOrder; }
  public isDespatchNote() { return this === EnumDocumentType.despatchNote; }
  public isDutyReturn() { return this === EnumDocumentType.dutyReturn; }
  public isFlatGrid() { return this === EnumDocumentType.flatGrid; }
  public isGroupGrid() { return this === EnumDocumentType.groupGrid; }
  public isInvoiceDataExchange() { return this === EnumDocumentType.invoiceDataExchange; }
  public isDeliverySummarySheet() { return this === EnumDocumentType.deliverySummarySheet; }
  public isDriverManifest() { return this === EnumDocumentType.driverManifest; }
  public isMaterialLabel() { return this === EnumDocumentType.materialLabel; }
  public isBeerflexDeliveryNote() { return this === EnumDocumentType.beerflexDeliveryNote; }
  public isPriceList() { return this === EnumDocumentType.priceList; }
  public isOutletPriceList() { return this === EnumDocumentType.outletPriceList; }
  public isGermanBeerDutyReturn() { return this === EnumDocumentType.germanBeerDutyReturn; }
  public isAustralianDutyReturn() { return this === EnumDocumentType.australianDutyReturn; }
  public isAccountsPackageCsv() { return this === EnumDocumentType.accountsPackageCsv; }
  public isStockTransferNote() { return this === EnumDocumentType.stockTransferNote; }
  public isUsSmallBrewersReport() { return this === EnumDocumentType.usSmallBrewersReport; }
  public isPalletManifest() { return this === EnumDocumentType.palletManifest; }
  public isPalletLabel() { return this === EnumDocumentType.palletLabel; }
  public isDespatchNotification() { return this === EnumDocumentType.despatchNotification; }
  public isAssemblyControlSheet() { return this === EnumDocumentType.assemblyControlSheet; }
  public isTaskCreatedNotification() { return this === EnumDocumentType.taskCreatedNotification; }
  public isTaskModifiedNotification() { return this === EnumDocumentType.taskModifiedNotification; }
  public isTasksDueNotification() { return this === EnumDocumentType.tasksDueNotification; }
  public isAddressLabel() { return this === EnumDocumentType.addressLabel; }
  public isUkBeerNewDutyReturn() { return this === EnumDocumentType.ukBeerNewDutyReturn; }
  public isUkWineAndOtherNewDutyReturn() { return this === EnumDocumentType.ukWineAndOtherNewDutyReturn; }
  public isProFormaInvoice() { return this === EnumDocumentType.proFormaInvoice; }
  public isProcessControlSheet() { return this === EnumDocumentType.processControlSheet; }
  public isStockAdjustment() { return this === EnumDocumentType.stockAdjustment; }
  public isBatchControlSheet() { return this === EnumDocumentType.batchControlSheet; }
  public isLabelOrderInvoice() { return this === EnumDocumentType.labelOrderInvoice; }
  public isOrderPickingList() { return this === EnumDocumentType.orderPickingList; }
  public isGroupedPriceList() { return this === EnumDocumentType.groupedPriceList; }
  public isGroupedOutletPriceList() { return this === EnumDocumentType.groupedOutletPriceList; }
  public isUllageRecord() { return this === EnumDocumentType.ullageRecord; }

  public static getValues(): readonly EnumDocumentType[] {
    return EnumDocumentType.allValues;
  }

  public static getValuesExcept0(): readonly EnumDocumentType[] {
    return EnumDocumentType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumDocumentType {
    const found = EnumDocumentType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumDocumentType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumDocumentType): EnumDocumentType {
    const found = name ? EnumDocumentType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumDocumentType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumDocumentType>): boolean {
    return this.key === other?.key;
  }
}